/**
 * The national map: the study sites on the shared satellite basemap
 * (components/BaseMap.tsx), fitted to India or to one state.
 *
 * Markers are DOM, so every label renders at its true size whatever the zoom.
 * Marker area scales with the reservoir storage the registry confirmed; a site
 * with no confirmed storage gets a fixed marker, and no storage is drawn for it.
 */

import { useMemo, useState } from 'react'
import { Marker } from 'react-map-gl/maplibre'
import type { LayerSpecification, SourceSpecification } from 'maplibre-gl'

import { CollapsibleLegend } from './MapCorner'
import { BaseMap, type Bounds } from './BaseMap'
import { geoUrl, useGeo } from './geo'
import { C } from '../design/tokens'

export const HAZARDS: Record<string, { label: string; colour: string }> = {
  DAM_FAILURE: { label: 'Dam failure', colour: C.hazardDam },
  FLASH_FLOOD: { label: 'Flash flood', colour: C.hazardFlash },
}

export const hazardColour = (hazard: string) => HAZARDS[hazard]?.colour ?? C.mapInfraOut
export const hazardLabel = (hazard: string) =>
  HAZARDS[hazard]?.label ?? hazard.charAt(0) + hazard.slice(1).toLowerCase().replace(/_/g, ' ')

export interface MapSite {
  id: string
  display_name: string
  state: string | null
  river: string | null
  hazard_class: string
  lat: number
  lon: number
  /** Only a volume the registry confirmed; null means it is not drawn. */
  volume_m3: number | null
  has_results: boolean
}

/** Storage, in the unit that keeps two significant figures readable. */
export function storage(m3: number): string {
  return m3 >= 1e8 ? `${(m3 / 1e9).toFixed(2)} km³` : `${(m3 / 1e6).toFixed(1)} Mm³`
}

/** India's mainland and islands, west-south-east-north. */
const INDIA: Bounds = [68.0, 6.5, 97.6, 36.8]

const SOURCES: Record<string, SourceSpecification> = {
  states: { type: 'geojson', data: geoUrl('india_states.geojson') },
}
const LAYERS: LayerSpecification[] = [
  { id: 'state-lines', type: 'line', source: 'states', paint: { 'line-color': C.mapBoundary, 'line-width': 0.75, 'line-opacity': 0.8 } },
]

function bboxOf(geometry: { type: string; coordinates: unknown }): Bounds {
  let w = 180
  let e = -180
  let s = 90
  let n = -90
  const polys =
    geometry.type === 'Polygon' ? [geometry.coordinates as number[][][]] : (geometry.coordinates as number[][][][])
  for (const poly of polys) {
    for (const [lo, la] of poly[0]) {
      w = Math.min(w, lo)
      e = Math.max(e, lo)
      s = Math.min(s, la)
      n = Math.max(n, la)
    }
  }
  return [w, s, e, n]
}

export function SitesMap({
  sites,
  activeSiteId = null,
  focusState = null,
  onSelectSite,
  legend = true,
  children,
}: {
  sites: MapSite[]
  activeSiteId?: string | null
  /** Fit the view to this state instead of the whole country. */
  focusState?: string | null
  onSelectSite?: (siteId: string) => void
  legend?: boolean
  /** Overlay content, positioned by the caller inside the map box. */
  children?: React.ReactNode
}) {
  const states = useGeo('india_states.geojson')
  const [hover, setHover] = useState<string | null>(null)

  const bounds: Bounds = useMemo(() => {
    if (!focusState) return INDIA
    const f = states.data?.features.find((s) => s.properties.name === focusState)
    if (!f) return INDIA
    const [w, s, e, n] = bboxOf(f.geometry)
    const padLon = (e - w) * 0.25 || 1
    const padLat = (n - s) * 0.25 || 1
    return [w - padLon, s - padLat, e + padLon, n + padLat]
  }, [focusState, states.data])

  const maxVolume = Math.max(1, ...sites.map((s) => s.volume_m3 ?? 0))
  const radius = (s: MapSite) => (s.volume_m3 ? 7 + 13 * Math.sqrt(s.volume_m3 / maxVolume) : 8)

  return (
    <div className="flex h-full w-full flex-col overflow-hidden" data-testid="sites-map">
      <div className="relative min-h-0 flex-1">
      <BaseMap bounds={bounds} sources={SOURCES} layers={LAYERS} padding={32} dragRotate={false} maxPitch={0}>
        {sites.map((s) => {
          const r = radius(s)
          const colour = hazardColour(s.hazard_class)
          const active = s.id === activeSiteId || s.id === hover
          return (
            <Marker key={s.id} longitude={s.lon} latitude={s.lat} anchor="center" style={{ zIndex: active ? 3 : 2 }}>
              <button
                type="button"
                data-site={s.id}
                className="relative block cursor-pointer"
                style={{ width: 2 * r, height: 2 * r }}
                onMouseEnter={() => setHover(s.id)}
                onMouseLeave={() => setHover((h) => (h === s.id ? null : h))}
                onClick={() => onSelectSite?.(s.id)}
                aria-label={s.display_name}
              >
                <span
                  className="absolute inset-0 rounded-full"
                  style={{ background: colour, opacity: 0.28, border: `${active ? 2.5 : 1.5}px solid ${colour}` }}
                />
                <span className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: colour }} />
                <span
                  className="pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-[4px] bg-slate-950/85 px-1.5 py-0.5 text-14 font-medium text-[var(--ink-text)]"
                  style={{ left: 2 * r + 8 }}
                >
                  {s.display_name}
                </span>
                {s.id === hover && (
                  <span className="panel pointer-events-none absolute w-[264px] p-4 text-left" style={{ left: 2 * r + 8, top: 2 * r + 12 }}>
                    <span className="block text-16 font-medium leading-5 text-slate-100">{s.display_name}</span>
                    <dl className="mt-3 space-y-1.5">
                      <Row label="State" value={s.state} />
                      <Row label="River" value={s.river} />
                      <Row label="Hazard" value={hazardLabel(s.hazard_class)} />
                      <Row label="Storage" value={s.volume_m3 ? storage(s.volume_m3) : null} />
                      <Row label="Results" value={s.has_results ? 'On file' : 'None'} />
                    </dl>
                  </span>
                )}
              </button>
            </Marker>
          )
        })}
      </BaseMap>

      {children}
      </div>
      {legend && sites.length > 0 && (
        <div className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-1 border-t border-[var(--ink-border)] bg-[var(--ink-surface)] px-4 py-2" data-testid="sites-legend">
          <CollapsibleLegend title="Hazard">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {Object.entries(HAZARDS)
              .filter(([key]) => sites.some((s) => s.hazard_class === key))
              .map(([key, h]) => (
                <div key={key} className="flex items-center gap-2 text-14 text-slate-300">
                  <span className="relative block h-4 w-4 rounded-full" style={{ border: `1.5px solid ${h.colour}` }}>
                    <span className="absolute inset-0 rounded-full" style={{ background: h.colour, opacity: 0.28 }} />
                  </span>
                  {h.label}
                </div>
              ))}
          {sites.some((s) => s.volume_m3) && (
            <span className="text-12 text-slate-400">Marker area scales with reservoir storage</span>
          )}
          </div>
          </CollapsibleLegend>
        </div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string | null }) {
  if (!value) return null
  return (
    <span className="flex items-baseline justify-between gap-3">
      <dt className="section-label">{label}</dt>
      <dd className="text-14 tabular-nums text-slate-200">{value}</dd>
    </span>
  )
}
